import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DUMMY_PROJECTS = [
  {
    _id: "demo-1",
    title: "AI-Powered Analytics Dashboard",
    description: "Real-time business intelligence platform with ML-driven insights and anomaly detection across 50+ data sources.",
    category: "AI / ML",
    bannerImage: null,
    bannerColor: "linear-gradient(135deg, #0D2929 0%, #1B4A4A 100%)",
  },
  {
    _id: "demo-2",
    title: "Supply Chain Automation",
    description: "End-to-end logistics optimisation using multi-agent AI systems and predictive modelling.",
    category: "Automation",
    bannerImage: null,
    bannerColor: "linear-gradient(135deg, #0A1F2E 0%, #163348 100%)",
  },
  {
    _id: "demo-3",
    title: "Healthcare SaaS Platform",
    description: "HIPAA-compliant patient management with AI-assisted diagnostics, scheduling, and billing reconciliation.",
    category: "Web App",
    bannerImage: null,
    bannerColor: "linear-gradient(135deg, #1A1A2E 0%, #2D2D4A 100%)",
  },
  {
    _id: "demo-4",
    title: "Real-Time Fraud Detection",
    description: "Streaming ML pipeline processing 10M+ transactions per day with sub-50ms flagging latency.",
    category: "AI / ML",
    bannerImage: null,
    bannerColor: "linear-gradient(135deg, #0D2929 0%, #2D5C5A 100%)",
  },
  {
    _id: "demo-5",
    title: "Enterprise Data Lakehouse",
    description: "Cloud-native data infrastructure on AWS with unified analytics for 500GB+ daily ingestion.",
    category: "Cloud",
    bannerImage: null,
    bannerColor: "linear-gradient(135deg, #0A1A1A 0%, #1B3535 100%)",
  },
  {
    _id: "demo-6",
    title: "Customer Intelligence API",
    description: "Behavioural segmentation and LTV prediction API serving personalised recommendations at scale.",
    category: "API",
    bannerImage: null,
    bannerColor: "linear-gradient(135deg, #1A0D2E 0%, #2E1A4A 100%)",
  },
];

function CardSkeleton() {
  return (
    <div
      style={{
        background: "#111111",
        border: "1px solid #1e1e1e",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <div style={{ paddingTop: "56.25%", background: "#1a1a1a", position: "relative", overflow: "hidden" }}>
        <div className="shimmer-bar" />
      </div>
      <div style={{ padding: "18px" }}>
        <div
          style={{
            height: "14px",
            background: "#222",
            borderRadius: "4px",
            marginBottom: "10px",
            width: "70%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div className="shimmer-bar" />
        </div>
        <div
          style={{
            height: "12px",
            background: "#1a1a1a",
            borderRadius: "4px",
            width: "90%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div className="shimmer-bar" />
        </div>
      </div>
    </div>
  );
}

// NOTE: ProjectCard's hover/visual styling (.gt-fp-card, .gt-fp-img, .gt-fp-img-inner,
// .gt-fp-arrow-btn) lives in the <style> block rendered by PortfolioSection below.
// Rendering ProjectCard without PortfolioSection also mounted loses that styling.
export function ProjectCard({ project }) {
  return (
    <Link
      to={`/project/${project.id || project._id}`}
      aria-label={`View case study: ${project.title}`}
      className="gt-fp-link"
      style={{ textDecoration: "none" }}
    >
      <div
        className="gt-fp-card"
        style={{
          position: "relative",
          background: "linear-gradient(145deg, #141f1e 0%, #0c1717 100%)",
          border: "1px solid rgba(91,148,144,0.18)",
          borderRadius: "12px",
          overflow: "hidden",
          cursor: "pointer",
          boxShadow: "0 0 20px rgba(91,148,144,0.08), 0 4px 16px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        {/* Card image — banner or logo based on cardImageType */}
        {(() => {
          const useLogo = project.cardImageType === "logo" && project.logoImage;
          const useBanner = !useLogo && project.bannerImage;
          const stat = project.outcomeStats?.[0];
          return (
            <div
              className="ps-card-img gt-fp-img"
              style={{
                position: "relative",
                paddingTop: "56.25%",
                overflow: "hidden",
                background: useLogo || useBanner
                  ? "linear-gradient(135deg, #0D1F1E, #142222)"
                  : project.bannerColor || "linear-gradient(135deg, #0D1F1E, #142222)",
              }}
            >
              <div className="gt-fp-img-inner" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {useBanner && (
                  <img
                    src={project.bannerImage}
                    alt={project.title}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  />
                )}
                {useLogo && (
                  <img
                    src={project.logoImage}
                    alt={project.title}
                    style={{ maxWidth: "60%", maxHeight: "70%", objectFit: "contain", position: "relative", zIndex: 1 }}
                  />
                )}
              </div>
              {stat?.value && (
                <div
                  data-testid="card-metric"
                  style={{
                    position: "absolute",
                    top: "14px",
                    left: "14px",
                    zIndex: 2,
                    maxWidth: "calc(100% - 28px)",
                    display: "inline-flex",
                    alignItems: "baseline",
                    gap: "8px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    background: "rgba(8,14,13,0.78)",
                    backdropFilter: "blur(6px)",
                    WebkitBackdropFilter: "blur(6px)",
                    border: "1px solid rgba(91,148,144,0.55)",
                    borderRadius: 9999,
                    padding: "7px 16px",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 300,
                      fontSize: 17,
                      lineHeight: 1,
                      color: "#b8e5e2",
                      textShadow: "0 0 14px rgba(91,148,144,0.5)",
                      letterSpacing: "-0.02em",
                      flexShrink: 0,
                    }}
                  >
                    {stat.value}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      lineHeight: 1,
                      color: "rgba(184,229,226,0.8)",
                      letterSpacing: "0.02em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {stat.label}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Card content */}
        <div style={{ padding: "20px 56px 20px 18px" }}>
          {project.category && (
            <div
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontSize: "10px",
                fontWeight: 500,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#a8dbd8",
                whiteSpace: "nowrap",
              }}
            >
              {project.category}
            </div>
          )}
          <div
            className="ps-card-title"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "18px",
              fontWeight: 500,
              color: "#fff",
              marginTop: "8px",
              letterSpacing: "-0.01em",
              lineHeight: 1.3,
            }}
          >
            {project.title}
          </div>
          <p
            className="ps-card-desc"
            style={{
              fontSize: "12px",
              fontWeight: 300,
              color: "rgba(255,255,255,0.5)",
              margin: 0,
              lineHeight: 1.6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {project.description}
          </p>
        </div>
        <div className="gt-fp-arrow-btn" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </div>
      </div>
    </Link>
  );
}

const PortfolioSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const headingRef = useRef(null);  // on the container div → children are h2 + link
  const tealSpanRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/portfolio`)
      .then((res) => {
        const data = res.data?.success ? res.data.data : res.data;
        if (Array.isArray(data) && data.length > 0) {
          setProjects(data.slice(0, 3));
        } else {
          setProjects(DUMMY_PROJECTS.slice(0, 3));
        }
      })
      .catch(() => setProjects(DUMMY_PROJECTS.slice(0, 3)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return; // wait until cards are rendered
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !gridRef.current) return;

    const ctx = gsap.context(() => {
      // Stagger h2 + link, then teal glow pulse — matches MidCta rhythm
      gsap.from(Array.from(headingRef.current.children), {
        y: 28,
        opacity: 0,
        stagger: 0.13,
        duration: 0.72,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 87%",
          once: true,
          onEnter: () => {
            setTimeout(() => {
              gsap.fromTo(
                tealSpanRef.current,
                { textShadow: "0 0 0px transparent" },
                { textShadow: "0 0 22px rgba(123,181,177,0.55)", duration: 0.4, yoyo: true, repeat: 1, ease: "power2.inOut" }
              );
            }, 420);
          },
        },
      });

      // Cards stagger — first row immediately, second row after 120ms head-start
      const allCards = Array.from(gridRef.current.children);
      const row1 = allCards.slice(0, 3);
      const row2 = allCards.slice(3);

      gsap.from(row1, {
        y: 20, opacity: 0, scale: 0.95,
        stagger: 0.07, duration: 0.6, ease: "power2.out",
        scrollTrigger: { trigger: gridRef.current, start: "top 85%", once: true },
      });

      gsap.from(row2, {
        y: 20, opacity: 0, scale: 0.95,
        stagger: 0.07, duration: 0.6, ease: "power2.out",
        delay: 0.12,
        scrollTrigger: { trigger: gridRef.current, start: "top 85%", once: true },
      });
    });

    return () => ctx.revert();
  }, [loading]);

  return (
    <section
      id="portfolio"
      className="portfolio-section-outer"
      style={{
        background: "transparent",
        padding: "96px 80px",
      }}
    >
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .shimmer-bar {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.05) 40%,
            rgba(255,255,255,0.10) 50%,
            rgba(255,255,255,0.05) 60%,
            transparent 100%
          );
          animation: shimmer 1.6s ease-in-out infinite;
        }
        .gt-fp-card {
          transition: transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94), border-color 0.3s, box-shadow 0.3s;
        }
        .gt-fp-card:hover {
          transform: translateY(-4px);
          border-color: rgba(91,148,144,0.5);
          box-shadow: 0 0 40px rgba(91,148,144,0.22), 0 10px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.07);
        }
        .gt-fp-img::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(91,148,144,0) 0%, rgba(45,92,90,0.28) 100%);
          opacity: 0;
          transition: opacity 0.35s;
          pointer-events: none;
        }
        .gt-fp-card:hover .gt-fp-img::after {
          opacity: 1;
        }
        .gt-fp-img-inner {
          transition: transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94);
        }
        .gt-fp-card:hover .gt-fp-img-inner {
          transform: scale(1.05);
        }
        .gt-fp-arrow-btn {
          position: absolute;
          bottom: 16px;
          right: 16px;
          z-index: 2;
          width: 34px;
          height: 34px;
          border-radius: 9999px;
          background: rgba(91,148,144,0.12);
          border: 1px solid rgba(91,148,144,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #a8dbd8;
          transition: background 0.25s, transform 0.25s, border-color 0.25s;
        }
        .gt-fp-card:hover .gt-fp-arrow-btn {
          background: rgba(91,148,144,0.32);
          border-color: rgba(91,148,144,0.7);
          transform: translateX(3px);
        }
        .gt-fp-link:focus-visible {
          outline: 2px solid rgba(91,148,144,0.6);
          outline-offset: 2px;
          border-radius: 12px;
        }
        .gt-fp-link:focus-visible .gt-fp-card {
          transform: translateY(-4px);
          border-color: rgba(91,148,144,0.5);
          box-shadow: 0 0 40px rgba(91,148,144,0.22), 0 10px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.07);
        }
        .gt-fp-link:focus-visible .gt-fp-img::after {
          opacity: 1;
        }
        .gt-fp-link:focus-visible .gt-fp-img-inner {
          transform: scale(1.05);
        }
        .gt-fp-link:focus-visible .gt-fp-arrow-btn {
          background: rgba(91,148,144,0.32);
          border-color: rgba(91,148,144,0.7);
          transform: translateX(3px);
        }
      `}</style>
      <div>
        <div
          ref={headingRef}
          style={{
            textAlign: "center",
            marginBottom: "48px",
          }}
        >
          <h2
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "clamp(32px, 5vw, 60px)",
              fontWeight: 300,
              color: "#fff",
              margin: "0 0 16px",
              letterSpacing: "clamp(-0.5px, -0.08vw, -1px)",
            }}
          >
            Featured{" "}
            <span ref={tealSpanRef} style={{ color: "#7BB5B1" }}>Projects</span>
          </h2>
          <Link
            to="/portfolio"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "13px",
              fontWeight: 500,
              color: "#7BB5B1",
              textDecoration: "none",
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#7BB5B1")}
          >
            View all projects →
          </Link>
        </div>

        <div
          ref={gridRef}
          className="portfolio-section-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "12px",
          }}
        >
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <CardSkeleton key={i} />
              ))
            : projects.map((project) => (
                <ProjectCard key={project.id || project._id} project={project} />
              ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
